import { Box, Grid, Modal, Radio, TextField, InputAdornment, CircularProgress, Button } from "@mui/material";
import { RadioProps } from '@mui/material/Radio';
import { FC, useState, useRef } from "react";
import { styled } from '@mui/material/styles';
import { useAppDispatch } from "../../store/hooks";
import { adjustPartyOpeningBalance } from '../../store/customerStore/customerThunk';
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import LoadingButton from "@mui/lab/LoadingButton";

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    transition: '0ms',
    width: 425,
    borderRadius: 2,
    bgcolor: 'background.paper',
    border: 'none',
    boxShadow: 24,
};

const BpIcon = styled('span')(({ theme }) => ({
    borderRadius: '50%',
    width: 16,
    height: 16,
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 0 0 1px rgb(16 22 26 / 40%)'
        : 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: theme.palette.mode === 'dark' ? '#394b59' : '#f5f8fa',
    backgroundImage:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))'
        : 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
    '.Mui-focusVisible &': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
    'input:hover ~ &': {
      backgroundColor: theme.palette.mode === 'dark' ? '#30404d' : '#ebf1f5',
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background:
        theme.palette.mode === 'dark' ? 'rgba(57,75,89,.5)' : 'rgba(206,217,224,.5)',
    },
}));

const BpCheckedIcon = styled(BpIcon)({
    backgroundColor: '#137cbd',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&:before': {
        display: 'block',
        width: 16,
        height: 16,
        backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
        content: '""',
    },
    'input:hover ~ &': {
        backgroundColor: '#106ba3',
    },
});

function BpRadio(props: RadioProps) {
    return (
        <Radio
        disableRipple
        color="default"
        checkedIcon={<BpCheckedIcon />}
        icon={<BpIcon />}
        {...props}
        />
    );
}

const schema = yup.object().shape({
    amount: yup.string().required("Adjustment amount is required"),
});

interface OpeningBalanceFormInputs {
    amount: string;
    remarks?: string;
}

interface AdjustPartyOpeningBalanceProps {
    open: boolean;
    onClose: (isSaved: boolean) => void;
    partyId: string;
    partyName: string;
    currentOpeningBalance: {
        amount: number;
        balanceType: 'Dr' | 'Cr';
    };
}

function calculateNewOpeningBalance(
    currentAmount: number,
    currentType: 'Dr' | 'Cr',
    adjustmentType: 'increase' | 'decrease',
    changeAmount: number
): { newAmount: number; newBalanceType: 'Dr' | 'Cr' } {
    let newAmount: number;
    let newBalanceType: 'Dr' | 'Cr';

    if (currentType === 'Dr') {
        if (adjustmentType === 'increase') {
            newAmount = currentAmount + changeAmount;
            newBalanceType = 'Dr';
        } else {
            if (currentAmount >= changeAmount) {
                newAmount = currentAmount - changeAmount;
                newBalanceType = 'Dr';
            } else {
                newAmount = changeAmount - currentAmount;
                newBalanceType = 'Cr';
            }
        }
    } else {
        if (adjustmentType === 'increase') {
            newAmount = currentAmount + changeAmount;
            newBalanceType = 'Cr';
        } else {
            if (currentAmount >= changeAmount) {
                newAmount = currentAmount - changeAmount;
                newBalanceType = 'Cr';
            } else {
                newAmount = changeAmount - currentAmount;
                newBalanceType = 'Dr';
            }
        }
    }

    return {
        newAmount: Number(newAmount.toFixed(2)),
        newBalanceType
    };
}

const AdjustPartyOpeningBalance: FC<AdjustPartyOpeningBalanceProps> = ({
    open,
    onClose,
    partyId,
    partyName,
    currentOpeningBalance
}) => {
    const dispatch = useAppDispatch();

    const [adjustmentType, setAdjustmentType] = useState<'increase' | 'decrease'>('increase');
    const [finalBalanceValue, setFinalBalanceValue] = useState(0);
    const [loading, setLoading] = useState(false);

    const methods = useForm<OpeningBalanceFormInputs>({
        resolver: yupResolver(schema),
    });
    const formRef = useRef<any>();

    const handleClose = () => {
        methods.reset();
        setAdjustmentType('increase');
        setFinalBalanceValue(0);
        onClose(false);
    }

    const onSubmit = async (data: OpeningBalanceFormInputs) => {
        setLoading(true);

        try {
            const payload = {
                partyId,
                adjustment: {
                    adjustmentType: adjustmentType,
                    amount: Number(data.amount),
                    reason: data.remarks || 'Opening balance adjustment',
                    description: data.remarks || '',
                    remarks: data.remarks || ''
                }
            };

            await dispatch(adjustPartyOpeningBalance(payload)).unwrap();
            onClose(true);
        } catch (error: any) {
            console.error('Error adjusting opening balance:', error);
        } finally {
            setLoading(false);
        }
    };

    const finalBalance = finalBalanceValue > 0
        ? calculateNewOpeningBalance(
            currentOpeningBalance.amount,
            currentOpeningBalance.balanceType,
            adjustmentType,
            finalBalanceValue
        )
        : { newAmount: currentOpeningBalance.amount, newBalanceType: currentOpeningBalance.balanceType };

    return(
        <Modal
            open={open}
            onClose={(event : any, reason:any) => {
                if (reason !== 'backdropClick') {
                    handleClose();
                }
            }}
        >
            <FormProvider {...methods}>
            <Box sx={style} component="form" onSubmit={methods.handleSubmit(data => onSubmit(data))} ref={formRef} noValidate>
            <Grid container >
                <Grid xs={12} sm={8} md={4} lg={12} p={2} pl={2} bgcolor='#e9ecf1' style={{borderTopLeftRadius:8,borderTopRightRadius:8,height:50}} >
                <div style={{float:'right',marginTop:-7,marginRight:-5}}>
                    <LoadingButton
                      type="submit"
                      name={'submit'}
                      loading={loading}
                      style={{color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff', marginRight:10 }}
                      variant="contained"
                      size='small'>
                      {`Save`}
                    </LoadingButton>

                    <Button
                        style={{color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff', marginRight:10 }}
                        variant="contained"
                        size='small'
                        onClick={handleClose} >
                        Cancel
                    </Button>
                </div>
                <h3  style={{marginTop:-2}}>Adjust Opening Balance </h3>
                </Grid>
            </Grid>

            {!partyName ? <CircularProgress></CircularProgress> :
            <Grid container p={2} spacing={2}>
                <Grid item xs={12} >
                    <h5 style={{fontWeight:'normal',color:'#9da7b7',fontSize:14}}>Party Name</h5>
                    <h5 style={{fontWeight:'normal',color:'#000',fontSize:15}}>{String(partyName).length > 100 ? String(partyName).substring(0,100) + '...' : String(partyName)}</h5>
                </Grid>
                <Grid item xs={8} >
                    <h5 style={{fontWeight:'normal',color:'#9da7b7',fontSize:14}}>Add Or Reduce Balance</h5>
                    <div style={{marginLeft:-5}}>
                    <BpRadio  checked={adjustmentType === 'increase'} onChange={()=> {
                        setAdjustmentType('increase');
                    }} size="small" style={{marginRight:-3}}/>
                    <label onClick={()=> {
                        setAdjustmentType('increase');
                    }}>Add Balance</label>
                    <BpRadio checked={adjustmentType === 'decrease'} onChange={()=> {
                        setAdjustmentType('decrease');
                    }} size="small" style={{marginRight:-3,marginLeft:5}}/>
                    <label onClick={()=> {
                        setAdjustmentType('decrease');
                    }}>Reduce Balance</label>
                    </div>
                </Grid>

                <Grid item xs={4} >
                    <h5 style={{fontWeight:'normal',color:'#9da7b7',fontSize:14}}>Current Balance</h5>
                    <h5 style={{fontWeight:'normal',color:'#000',fontSize:15,marginBottom:10}}>
                        {currentOpeningBalance.amount} {currentOpeningBalance.balanceType}</h5>
                </Grid>

                <Grid item xs={8} pr={2}>
                    <h5 style={{fontWeight:'normal',color:'#9da7b7',fontSize:14}}>Add Or Reduce Balance</h5>
                    <TextField placeholder="Enter Amount" required size="small"
                        {...methods.register('amount')}
                        type="number"
                        className='c-text-field-4'
                        variant="standard"
                        sx={{width:'100%',mt:0.5,borderRadius:'8px!important'}}
                        onChange={(e) => setFinalBalanceValue(Number(e.target.value))}
                        InputProps={{
                        disableUnderline: true,
                        endAdornment:<InputAdornment position="start" style={{marginTop:-5}}>₹</InputAdornment>
                        }}
                    />
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.amount?.message ?? ""}</p>
                </Grid>

                <Grid item xs={4} >
                    <h5 style={{fontWeight:'normal',color:'#9da7b7',fontSize:14}}>Final Balance</h5>
                    <h5 style={{fontWeight:'normal',color:'#000',fontSize:15,marginBottom:10}}>
                        {finalBalance.newAmount} {finalBalance.newBalanceType}</h5>
                </Grid>

                <Grid item xs={12} >
                    <h5 style={{fontWeight:'normal',color:'#9da7b7',fontSize:14}}>Remarks (Optional)</h5>
                    <TextField
                        className='c-text-field-4'
                        variant="standard"
                        size="small"
                        {...methods.register('remarks')}
                        multiline
                        rows={4}
                        sx={{width:'100%',mt:0.5,borderRadius:'8px!important',height:100}}
                        placeholder='Reason for adjusting the balance ...'
                        InputProps={{
                            disableUnderline: true
                        }}
                    />
                </Grid>
            </Grid>
            }
            </Box>
            </FormProvider>
        </Modal>
    )
};
export default AdjustPartyOpeningBalance;
